import { Category } from '@modules/common/types';
import { getReasonOrEntityLabels } from '@modules/common/utils';
import { getLabelsToCategoryPairArray } from '@modules/feedback/components/FeedbackCard/FeedbackCategories/utils';
import { getSelectedLabels } from '@modules/feedback/components/FeedbackCard/utils';
import { isFeedbackSummaryPresent } from '@modules/feedback/components/FeedbackSummary/utils';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import { useCallback, useEffect } from 'react';
import { usePrevious } from 'react-use';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';
import { SearchLabel } from 'v2/core/entities/feedback/labels';
import { FeedbackMetaData } from 'v2/store/useViewFeedbackStore';

interface HandleDetailViewPaginationOptions {
  feedbacks: Feedback[];
  currentFeedbackIndex: number;
  isFeedbackDetailsDrawerVisible: boolean;
  selectedCategories: Category[] | undefined;
  feedbackMetadata: FeedbackMetaData[] | undefined;
  setCurrentFeedbackInDetailView: (feedback: Feedback) => void;
  setCurrentFeedbackIndexInDetailView: (feedbackIndex: number) => void;
  setCurrentFeedbackSelectedLabelsInDetailView: React.Dispatch<
    React.SetStateAction<FeedbackLabel[]>
  >;
  feedbackListViewType: FeedbackListViewType;
}

export const useHandleDetailViewPagination = ({
  feedbacks,
  currentFeedbackIndex,
  isFeedbackDetailsDrawerVisible,
  selectedCategories,
  feedbackMetadata,
  setCurrentFeedbackInDetailView,
  setCurrentFeedbackIndexInDetailView,
  setCurrentFeedbackSelectedLabelsInDetailView,
  feedbackListViewType,
}: HandleDetailViewPaginationOptions) => {
  const previousFeedbackIndex = usePrevious(currentFeedbackIndex);
  const savedItemsMap = useSavedFeedbackListStore(
    ({ savedItemsMap }) => savedItemsMap,
  );

  const onPageChange = useCallback(
    (feedbackIndex: number) => {
      const feedback = feedbacks[feedbackIndex];
      setCurrentFeedbackInDetailView(feedback);
      setCurrentFeedbackIndexInDetailView(feedbackIndex);
    },
    [
      feedbacks,
      setCurrentFeedbackInDetailView,
      setCurrentFeedbackIndexInDetailView,
    ],
  );

  const getSavedLabels = useCallback(
    (feedback: Feedback) =>
      savedItemsMap[feedback?.id]?.snippetMetadata?.reduce(
        (labels, metadata) => {
          if (!metadata.labelID) {
            return labels;
          }

          const label = { id: metadata.labelID } as FeedbackMetaData;
          labels.push(label);
          return labels;
        },
        [] as FeedbackMetaData[],
      ) ?? [],
    [savedItemsMap],
  );

  const getSelectedCategoryLabels = useCallback(
    (feedback: Feedback) => {
      const reasonOrEntityLabels = getReasonOrEntityLabels(
        feedback.labels ?? [],
      );

      const categoryLabels = getLabelsToCategoryPairArray({
        labels: reasonOrEntityLabels,
      });

      return categoryLabels
        .filter(([category]) => selectedCategories?.includes(category))
        .flatMap(([category, labels]) =>
          labels.map(
            (label) => ({ id: label.id, category } as FeedbackMetaData),
          ),
        );
    },
    [selectedCategories],
  );

  const getSelectedFeedbackMetadata = useCallback(
    (feedback: Feedback) => {
      const savedLabels = getSavedLabels(feedback);

      const selectedFeedbackMetadata =
        feedbackListViewType === FeedbackListViewType.SAVED
          ? feedbackMetadata
          : savedLabels.length
          ? savedLabels
          : feedbackMetadata;

      const selectedCategoryLabels = getSelectedCategoryLabels(feedback);

      return !selectedFeedbackMetadata?.[0]?.id
        ? selectedCategoryLabels
        : selectedFeedbackMetadata;
    },
    [
      feedbackListViewType,
      feedbackMetadata,
      getSavedLabels,
      getSelectedCategoryLabels,
    ],
  );

  useEffect(() => {
    if (
      isFeedbackDetailsDrawerVisible &&
      previousFeedbackIndex !== undefined &&
      previousFeedbackIndex > -1 &&
      previousFeedbackIndex !== currentFeedbackIndex
    ) {
      const feedback = feedbacks[currentFeedbackIndex];

      const selectedFeedbackMetadata = getSelectedFeedbackMetadata(feedback);

      const isSummaryPresent = isFeedbackSummaryPresent(feedback);
      const labels = getSelectedLabels(
        feedback.labels,
        selectedFeedbackMetadata,
        isSummaryPresent,
      );

      setCurrentFeedbackSelectedLabelsInDetailView(
        selectedFeedbackMetadata?.length !== 0 ? labels : [],
      );

      const searchLabel = feedback.labels?.filter(
        (label): label is SearchLabel => label.type === 'SEARCH',
      )[0];
      if (!!searchLabel?.matches?.length) {
        setCurrentFeedbackSelectedLabelsInDetailView((prevState) => [
          ...prevState,
          searchLabel,
        ]);
      }
    }
  }, [
    currentFeedbackIndex,
    feedbacks,
    getSelectedFeedbackMetadata,
    isFeedbackDetailsDrawerVisible,
    previousFeedbackIndex,
    setCurrentFeedbackSelectedLabelsInDetailView,
  ]);

  return onPageChange;
};
